import moment from 'moment';
import { Item } from 'shared-types';
import { TranslationsFunc } from '@lang';
import type { BasketCardItem } from 'voyager';
import { FulfilableBasketItem } from '@features/AddedToBasket/Fulfilable/FulfilableAddedToBasket.types';
import {
  pluralizeAdults,
  pluralizeChildren,
  pluralizeGuests,
} from '../../BasketDrawer.helpers';

export type ToBasketCardItemDeps = {
  t: TranslationsFunc;
  currencyCode: string;
};

const isFulfilable = (item: Item | FulfilableBasketItem): item is FulfilableBasketItem =>
  'type' in item && (item.type === 'event' || item.type === 'voucher');

const formatDateRange = (checkIn?: string, checkOut?: string) => {
  if (!checkIn) return undefined;
  const start = moment(checkIn).format('ddd, D MMM');
  if (!checkOut) return start;
  return `${start} – ${moment(checkOut).format('ddd, D MMM')}`;
};

const formatDateTime = (date?: string) => {
  if (!date) return undefined;
  return `${moment(date).format('ddd, D MMM')} · ${moment(date).format('HH:mm')}`;
};

const guestCountLine = (adults: number | undefined, children: number | undefined, t: TranslationsFunc) => {
  const parts: string[] = [];
  if (adults && adults > 0) parts.push(`${adults} ${pluralizeAdults(adults, t)}`);
  if (children && children > 0) parts.push(`${children} ${pluralizeChildren(children, t)}`);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

const firstImageUrl = (item: Item): string | undefined =>
  item.bookable?.imageUrl ?? item.image?.url ?? item.imageGallery?.[0]?.url ?? undefined;

const compactMeta = (...lines: Array<string | undefined>): string[] =>
  lines.filter((line): line is string => Boolean(line));

const roomToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const meta = compactMeta(
    formatDateRange(item.checkIn, item.checkOut),
    guestCountLine(item.adults, item.children, deps.t),
  );
  const extras = (item.bookable?.roomExtras ?? []).map(
    (extra) => `${extra.name} × ${extra.quantity}`,
  );
  return {
    id: String(item.id),
    title: item.bookable?.name ?? item.name,
    imageUrl: firstImageUrl(item),
    meta,
    extras: extras.length > 0 ? extras : undefined,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const spaToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const guestDetails = item.guestDetails ?? [];
  const guestCount = guestDetails.length;
  const meta = compactMeta(
    formatDateTime(item.checkIn),
    guestCount > 0 ? `${guestCount} ${pluralizeGuests(guestCount, deps.t)}` : undefined,
  );

  const allExtras = guestDetails.flatMap((guest) => guest.extras ?? []);
  const groupedExtras = allExtras.reduce<Record<string, { name: string; count: number }>>(
    (acc, extra) => {
      const id = String(extra.id);
      if (!acc[id]) acc[id] = { name: extra.name, count: 0 };
      acc[id].count += 1;
      return acc;
    },
    {},
  );
  const extras = Object.values(groupedExtras).map((e) => `${e.name} × ${e.count}`);

  return {
    id: String(item.id),
    title: item.bookable?.name ?? item.name,
    imageUrl: firstImageUrl(item),
    meta,
    extras: extras.length > 0 ? extras : undefined,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const diningToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const meta = compactMeta(
    formatDateTime(item.checkIn),
    guestCountLine(item.adults, item.children, deps.t),
  );
  return {
    id: String(item.id),
    title: item.bookable?.ids?.restaurantName ?? item.bookable?.name ?? item.name,
    imageUrl: firstImageUrl(item),
    meta,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const productToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const quantity = item.quantity ?? 1;
  const meta = compactMeta(
    item.option?.name,
    `${deps.t('basketDrawer.productListing.quantity')}: ${quantity}`,
  );
  return {
    id: String(item.id),
    title: item.product?.name ?? item.name,
    imageUrl: firstImageUrl(item),
    meta,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const spaPackageToBasketCardItem = (item: Item, deps: ToBasketCardItemDeps): BasketCardItem => {
  const guestCount = item.guestDetails?.length ?? item.numberOfGuests ?? 0;
  const meta = compactMeta(
    formatDateRange(item.checkIn, item.checkOut),
    guestCount > 0 ? `${guestCount} ${pluralizeGuests(guestCount, deps.t)}` : undefined,
  );
  return {
    id: String(item.id),
    title: item.spaPackage?.name ?? item.bookable?.name ?? item.name,
    imageUrl: item.spaPackage?.image?.url ?? firstImageUrl(item),
    meta,
    price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
  };
};

const fulfilableToBasketCardItem = (
  item: FulfilableBasketItem,
  deps: ToBasketCardItemDeps,
): BasketCardItem => {
  const quantity = item.quantity ?? 1;
  const meta = compactMeta(
    item.eventId && item.detailsBag ? undefined : item.briefDescription,
    quantity > 1
      ? `${deps.t('basketDrawer.productListing.quantity')}: ${quantity}`
      : undefined,
  );
  // detailsBag prices are in minor units (priceAsInt); item.price is a number major units fallback
  const amount = item.detailsBag?.priceAsInt
    ? item.detailsBag.priceAsInt / 100
    : item.price ?? 0;
  return {
    id: String(item.id),
    title: item.detailsBag?.name ?? item.name,
    imageUrl: item.detailsBag?.images?.[0] ?? item.mainImageUrl,
    meta,
    price: { amount, currencyCode: deps.currencyCode },
  };
};

export const toBasketCardItem = (
  item: Item | FulfilableBasketItem,
  deps: ToBasketCardItemDeps,
): BasketCardItem => {
  if (isFulfilable(item)) return fulfilableToBasketCardItem(item, deps);

  switch (item.type) {
    case 'rooms':
      return roomToBasketCardItem(item, deps);
    case 'tables':
      return diningToBasketCardItem(item, deps);
    case 'products':
      return productToBasketCardItem(item, deps);
    case 'spa-packages':
      return spaPackageToBasketCardItem(item, deps);
    default:
      // Spa entities have varying types (spa-treatments, spa-classes, spa-days, etc.)
      if (item.type?.startsWith('spa')) return spaToBasketCardItem(item, deps);
      // Conservative fallback: render the item with its raw name + price.
      return {
        id: String(item.id),
        title: item.bookable?.name ?? item.name,
        imageUrl: firstImageUrl(item),
        price: { amount: item.price?.amount ?? 0, currencyCode: deps.currencyCode },
      };
  }
};

export const toBasketCardItems = (
  items: Array<Item | FulfilableBasketItem>,
  deps: ToBasketCardItemDeps,
): BasketCardItem[] => items.map((item) => toBasketCardItem(item, deps));
